import { messageOf, vmError } from './errors.js';
const BLOCK = 256; // v86's async disk snapshot block size, not the guest sector size
/**
 * v86 accepts a get/set/load disk object. Its built-in HTTP loader accepts
 * short 206 bodies and wrong Content-Range offsets as successful ATA reads.
 * Feeding those bytes to Linux can crash Node with SIGSEGV. Validate each
 * read before delivering it, and preserve v86's existing snapshot format.
 */
export class RangeDisk {
    filename;
    byteLength;
    onError;
    timeoutMs;
    retryMs;
    onload;
    blocks = new Map();
    writes = new Set();
    lifetime = new AbortController();
    failed = false;
    constructor(filename, byteLength, onError, timeoutMs = 20_000, retryMs = 500) {
        this.filename = filename;
        this.byteLength = byteLength;
        this.onError = onError;
        this.timeoutMs = timeoutMs;
        this.retryMs = retryMs;
    }
    load() { this.onload?.(Object.create(null)); }
    destroy() { this.lifetime.abort(); }
    get_from_cache(offset, length) {
        for (let i = 0; i < length / BLOCK; i++)
            if (!this.blocks.has(offset / BLOCK + i))
                return undefined;
        const bytes = new Uint8Array(length);
        this.overlay(offset, bytes);
        return bytes;
    }
    overlay(offset, bytes) {
        for (let i = 0; i < bytes.length / BLOCK; i++) {
            const block = this.blocks.get(offset / BLOCK + i);
            if (block)
                bytes.set(block, i * BLOCK);
        }
    }
    get(offset, length, done, opts) {
        if (this.failed || this.lifetime.signal.aborted || opts?.signal?.aborted)
            return;
        const cached = this.get_from_cache(offset, length);
        if (cached) {
            done(cached);
            return;
        }
        void this.read(offset, length, opts?.signal).then((bytes) => {
            if (this.failed || this.lifetime.signal.aborted || opts?.signal?.aborted)
                return;
            // A write can complete while the network read is pending.
            this.overlay(offset, bytes);
            done(bytes);
        }).catch((error) => {
            if (this.failed || this.lifetime.signal.aborted || opts?.signal?.aborted)
                return;
            this.failed = true;
            this.lifetime.abort();
            this.onError(vmError('download', `the VM disk read failed: ${messageOf(error)}`, error));
        });
    }
    get_and_cache(offset, length, done, opts) {
        this.get(offset, length, (bytes) => {
            for (let i = 0; i < length / BLOCK; i++) {
                const index = offset / BLOCK + i;
                if (!this.blocks.has(index))
                    this.blocks.set(index, bytes.slice(i * BLOCK, (i + 1) * BLOCK));
            }
            done(bytes);
        }, opts);
    }
    set(offset, bytes, done) {
        for (let i = 0; i < bytes.length / BLOCK; i++) {
            const index = offset / BLOCK + i;
            this.blocks.set(index, bytes.slice(i * BLOCK, (i + 1) * BLOCK));
            this.writes.add(index);
        }
        done();
    }
    get_buffer(done) { done(); }
    get_state() { return [[...this.writes].map((index) => [index, this.blocks.get(index)])]; }
    set_state(state) {
        this.blocks.clear();
        this.writes.clear();
        for (const [index, bytes] of state[0]) {
            this.blocks.set(index, bytes.slice());
            this.writes.add(index);
        }
    }
    async read(offset, length, signal) {
        if (offset % BLOCK || length % BLOCK || offset < 0 || length <= 0 || offset + length > this.byteLength) {
            throw new Error('invalid disk range');
        }
        let last;
        for (let attempt = 0; attempt < 4; attempt++) {
            if (this.lifetime.signal.aborted || signal?.aborted)
                throw new Error('disk read aborted');
            const controller = new AbortController();
            const abort = () => controller.abort();
            this.lifetime.signal.addEventListener('abort', abort, { once: true });
            signal?.addEventListener('abort', abort, { once: true });
            const timer = setTimeout(abort, this.timeoutMs);
            try {
                const end = offset + length - 1;
                const response = await fetch(this.filename, {
                    headers: { Range: `bytes=${offset}-${end}` }, signal: controller.signal,
                });
                const range = /^bytes (\d+)-(\d+)\/(\d+)$/.exec(response.headers.get('content-range') ?? '');
                const encoding = response.headers.get('content-encoding');
                if (response.status !== 206 || !range || +range[1] !== offset || +range[2] !== end ||
                    +range[3] !== this.byteLength || (encoding && encoding !== 'identity')) {
                    await response.body?.cancel();
                    throw new Error('the server returned a different disk range');
                }
                const bytes = new Uint8Array(await response.arrayBuffer());
                if (bytes.length !== length)
                    throw new Error(`incomplete disk range (${bytes.length} of ${length} bytes)`);
                return bytes;
            }
            catch (error) {
                last = error;
            }
            finally {
                clearTimeout(timer);
                this.lifetime.signal.removeEventListener('abort', abort);
                signal?.removeEventListener('abort', abort);
            }
            if (attempt < 3)
                await new Promise((resolve) => setTimeout(resolve, this.retryMs * 2 ** attempt));
        }
        throw last;
    }
}
//# sourceMappingURL=disk.js.map